import React, {useEffect, useState} from "react";
import axios from "../../utility/customAxios";
import {Button} from "../../components/ui/button";
import {cn} from "../../components/ui/lib/utils";
import {Dialog, DialogContent} from "../../components/ui/dialog";
import {
    DropDownItem,
    dropDownItemArrayToStringArray,
    MultiSelectorDropDown,
    stringArrayToDropDownItemArray
} from "../../components/Input/MultiSelectorDropdown/MultiSelectorDropDown";
import {Input} from "../../components/ui/input";
import {VerticalTabs} from "../../components/ui/vertical-tabs";
import {toast} from "../../components/ui/use-toast";
import AuthContext from "../../contexts/JWTContext";
import {KnownService} from "../../types/service";
import {LoadingSpinner} from "../../components/ui/customSpinner";
import {LogsComponent, LogSearch} from "../Logs";
import {Attribute} from "../../types/telemetry";
import {Transform} from "../../components/Dashboarding/widgets/TransformSelector";
import {DataTable} from "../../components/ui/datatable/datatable";
import {ColumnDef} from "@tanstack/react-table";
import {Badge} from "../../components/ui/badge";
import {useFeatureFlagsStore} from "../../store/featureFlagsStore";


interface NamespaceFilterArgs {
    namespaces: string[]
    regex: string
}

interface ServiceFilterArgs {
    services: string[]
    regex: string
}

interface CustomFilterArgs {
    filters: Map<string, string[]>;
    excludeFilters: Map<string, string[]>;
    regexes: string[]
    excludeRegexes: string[]
}

enum FilterType {
    Namespace = "namespace",
    Service = "service",
    Custom = "custom"
}

enum IncludeOrExclude {
    Include = "include",
    Exclude = "exclude"
}

// A filter is defined by the following
interface Filter {
    id: string
    // Empty means all environments
    environments: string[]
    // Whether the exclusion is inclusive or exclusive
    inclusionType: IncludeOrExclude
    // The type of exclusion
    filterType: FilterType
    // The args if the exclusion type is a namespace
    namespaceFilterArgs: NamespaceFilterArgs | undefined
    // The args if the exclusion type is a service
    serviceFilterArgs: ServiceFilterArgs | undefined
    // The args if the exclusion type is custom
    customFilterArgs: CustomFilterArgs | undefined
}

type TraceRedact = {
    uuid: string;
    environments: string[];
    serviceNames: string[];
    pattern: string;
    replacement: string;
}

interface TraceFilter {
    id: string;
    inclusionType: IncludeOrExclude;
    customTraceFilterArgs: {
        filters: Record<string, string[]>;
        excludeFilters: Record<string, string[]>;
    };
}

export function IngestionSettings() {
    const tabData = [
        {
            title: "Log Filters",
            content: <LogExclusionSettings/>
        },
        {
            title: "Trace Filters",
            content: <TraceExclusionSettings/>
        },
        {
            title: "Trace Redactions",
            content: <TraceRedactionSettings/>
        },
        {
            title: "Retention Settings",
            content: <RetentionSettings/>
        }
    ]

    return (
        <div className="flex flex-col space-y-4 p-4">
            <h2 className="text-xl font-bold">Ingestion Settings</h2>
            <div className={"text-textdark"}> You can add log filters or trace endpoint redactions for filter or redact
                sensitive data from being ingested.
            </div>
            <div className={"mt-4"}>
                <VerticalTabs data={tabData}/>
            </div>
        </div>
    )
}

export function LogExclusionSettings() {
    const [logFilters, setLogFilters] = React.useState<Filter[]>([]);

    // Delete modal
    const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean[]>([])
    const [triggerReload, setTriggerReload] = React.useState(false)
    const [openAddModal, setOpenAddModal] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(true)

    // Column definitions for the DataTable
    const columns: ColumnDef<Filter>[] = [
        {
            accessorKey: 'inclusionType',
            header: "Type",
            cell: ({ row }) => {
                return <div className="py-2 whitespace-nowrap">{row.original.inclusionType || "Unknown"}</div>
            },
        },
        {
            accessorKey: 'environments',
            header: "Environments",
            cell: ({ row }) => {
                return (
                    <div className="py-2">
                        {row.original.environments && row.original.environments.length > 0
                            ? row.original.environments.map((env, i) => (
                                <div key={i} className="leading-6">{env}</div>
                            ))
                            : "All"}
                    </div>
                )
            },
        },
        {
            accessorKey: 'filterType',
            header: "Class",
            cell: ({ row }) => {
                return <div className="py-2 whitespace-nowrap">{row.original.filterType}</div>
            },
        },
        {
            accessorKey: 'entities',
            header: "Entities",
            cell: ({ row }) => {
                const filter = row.original;
                return (
                    <div className="py-2">
                        {filter.filterType === FilterType.Service
                            ? (filter.serviceFilterArgs?.services && filter.serviceFilterArgs.services.length > 0
                                ? filter.serviceFilterArgs.services.map((service, i) => (
                                    <div key={i} className="leading-6">{service}</div>
                                ))
                                : "All")
                            : filter.filterType === FilterType.Namespace
                                ? (filter.namespaceFilterArgs?.namespaces && filter.namespaceFilterArgs.namespaces.length > 0
                                    ? filter.namespaceFilterArgs.namespaces.map((namespace, i) => (
                                        <div key={i} className="leading-6">{namespace}</div>
                                    ))
                                    : "All")
                                : filter.filterType === FilterType.Custom
                                    ? (
                                        <div>
                                            {filter.customFilterArgs?.filters && Object.entries(filter.customFilterArgs.filters).length > 0 ? (
                                                <div>
                                                    {Object.entries(filter.customFilterArgs.filters).map(([key, values], i) => (
                                                        <div key={i} className="leading-6">
                                                            {key} = {values.join('  || ')}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null}

                                            {filter.customFilterArgs?.excludeFilters && Object.entries(filter.customFilterArgs.excludeFilters).length > 0 ? (
                                                <div className="mt-2">
                                                    {Object.entries(filter.customFilterArgs.excludeFilters).map(([key, values], i) => (
                                                        <div key={i} className="leading-6">
                                                            {key} ≠ {values.join(' || ')}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null}

                                            {(!filter.customFilterArgs?.filters || Object.entries(filter.customFilterArgs.filters).length === 0) && 
                                             (!filter.customFilterArgs?.excludeFilters || Object.entries(filter.customFilterArgs.excludeFilters).length === 0) && 
                                             "-"}
                                        </div>
                                    )
                                    : "Custom Filter"}
                    </div>
                )
            },
        },
        {
            accessorKey: 'regex',
            header: "Regex",
            cell: ({ row }) => {
                const filter = row.original;
                return (
                    <div className="py-2 overflow-x-auto">
                        {filter.filterType !== FilterType.Custom ? (
                            <div className="whitespace-nowrap">
                                {filter.filterType === FilterType.Service 
                                    ? filter.serviceFilterArgs?.regex 
                                    : filter.filterType === FilterType.Namespace 
                                        ? filter.namespaceFilterArgs?.regex
                                        : ""}
                            </div>
                        ) : (
                            <div>
                                {filter.customFilterArgs?.regexes && filter.customFilterArgs.regexes.length > 0 ? (
                                    <div>
                                        ~ {filter.customFilterArgs.regexes.join(" && ")}
                                    </div>
                                ) : null}

                                {filter.customFilterArgs?.excludeRegexes && filter.customFilterArgs.excludeRegexes.length > 0 ? (
                                    <div className="mt-2">
                                        !~ {filter.customFilterArgs.excludeRegexes.join(" && ")}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            header: "",
            cell: ({ row }) => {
                const filter = row.original;
                const index = logFilters.findIndex(f => f.id === filter.id);
                return (
                    <div className="flex-none flex flex-col justify-center">
                        <Dialog open={openDeleteModal[index]}>
                            <DialogContent className={"w-1/3"}>
                                <div className={"flex flex-col gap-4 text-textmedium"}>
                                    <h3 className={"text-lg font-bold"}>Remove filter</h3>
                                    <div className={"text-textmedium"}>Are you sure you want to
                                        remove filter ?
                                    </div>
                                    <div className={"flex justify-end gap-4"}>
                                        <Button
                                            onClick={() => setOpenDeleteModal(prev => {
                                                prev[index] = false
                                                return [...prev]
                                            })}
                                            className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Cancel</Button>
                                        <Button
                                            onClick={() => {
                                                axios.delete("/api/v1/logFilter?id=" + filter.id).then(() => {
                                                    setTriggerReload(prev => !prev)
                                                }).catch(e => console.error(e))
                                                setOpenDeleteModal(prev => {
                                                    prev[index] = false
                                                    return [...prev]
                                                })
                                            }}
                                            className={"bg-red-500/20 border border-red-500 text-textmedium hover:text-textlight rounded"}>Remove</Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Button
                            requiredPermission={{
                                operation: "delete",
                                resource: "logFilters/default/*"
                            }}
                            onClick={() => {
                                setOpenDeleteModal(prev => {
                                    prev[index] = true
                                    return [...prev]
                                })
                            }}
                            className={"bg-red-500/20 h-[32px] border border-red-500 text-textmedium hover:text-textlight rounded"}>Remove</Button>
                    </div>
                )
            },
        },
    ];

    useEffect(() => {
        setIsLoading(true);
        axios.get("/api/v1/logFilters").then((response) => {
            setLogFilters(response.data.logFilters)
            setOpenDeleteModal(new Array(response.data.logFilters.length).fill(false))
            setIsLoading(false);
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load log filters";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Log Filters and Trace Redactions",
                description: errorMessage,
                variant: "destructive"
            });
            setIsLoading(false);
        })
    }, [triggerReload])

    return (
        <div className={""}>
            <div className={"flex justify-between"}>
                <div>
                    <h2 className={"text-lg font-bold text-textmedium"}>Log Filters</h2>
                    <p className={"text-sm text-textdark"}>
                        Add filters to prevent logs from being exported from your cluster. This helps protect sensitive
                        data and manage verbose logging.{" "}
                        <a
                            href="https://metoro.io/docs/logs/ingestion-settings"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        >
                            Learn more
                            <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                        </a>
                    </p>
                </div>
                <div className={"flex flex-col justify-center"}>
                    <NewLogFilterModal
                        openAddModal={openAddModal}
                        setOpenAddModal={setOpenAddModal}
                        setLogFilters={setLogFilters}
                        setTriggerReload={() => {
                            setTriggerReload(prev => !prev)
                        }}
                    />
                    <Button
                        requiredPermission={{
                            resource: "logFilters/default/*",
                            operation: "create"
                        }}
                        onClick={() => setOpenAddModal(true)}
                        className={"bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded"}>Add
                        Filter</Button>
                </div>
            </div>
            <div className="mt-4">
                <DataTable
                    columns={columns}
                    data={logFilters}
                    tableTitle="Log Filters"
                    filters={[]}
                    isLoading={isLoading}
                    emptyStateMessage="Add a log filter to prevent Metoro from exporting logs that match specific conditions."
                    className="bg-backgroundmedium"
                />
            </div>
        </div>
    );
}

function NewLogFilterModal(
    props: {
        openAddModal: boolean,
        setOpenAddModal: (open: boolean) => void,
        setLogFilters: (logFilters: Filter[]) => void,
        setTriggerReload: () => void
    }
) {
    const disallowedKeys = ["environment"]
    const [services, setServices] = React.useState<KnownService[]>([])
    const [namespaces, setNamespaces] = React.useState<string[]>([])

    // State for LogsComponent
    const [filter, setFilter] = useState<Map<string, string[]>>(new Map())
    const [excludeFilter, setExcludeFilter] = useState<Map<string, string[]>>(new Map())
    const [regexes, setRegexes] = useState<string[]>([])
    const [excludeRegexes, setExcludeRegexes] = useState<string[]>([])
    const [logAttributes, setLogAttributes] = useState<Map<string, Attribute[]>>(new Map())
    const [customColumns, setCustomColumns] = useState<string[]>([])
    const [transforms, setTransforms] = useState<Transform[]>([])

    // Function to set free text filter (required by LogsComponent but not used here)
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const [logFilter, setLogFilter] = React.useState<Filter>({
        id: "",
        environments: [],
        inclusionType: IncludeOrExclude.Exclude,
        filterType: FilterType.Service,
        namespaceFilterArgs: undefined,
        serviceFilterArgs: {
            services: [],
            regex: ".*"
        },
        customFilterArgs: {
            filters: new Map<string, string[]>(),
            excludeFilters: new Map<string, string[]>(),
            regexes: [],
            excludeRegexes: []
        }
    })
    const [environments, setEnvironments] = React.useState<DropDownItem[]>([])
    useEffect(() => {
        axios.get("/api/v1/environments").then((response) => {
            setEnvironments(stringArrayToDropDownItemArray(response.data.environments))
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load environments";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Environments",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, [])


    useEffect(() => {
        // Fetch environments
        axios.post("/api/v1/serviceSummaries", {
            environments: logFilter.environments,
            startTime: Math.floor(Date.now() / 1000 - 300),
            endTime: Math.floor(Date.now() / 1000),
        }).then((response) => {
            setServices(response.data.serviceSummaries)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load services";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Services",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, [logFilter.environments])


    // Get the namespaces
    useEffect(() => {
        let url = "/api/v1/namespaces"
        if (logFilter.environments.length > 0) {
            url = url + "?environments=" + logFilter.environments.join(",")
        }
        axios.get(url).then((response) => {
            setNamespaces(response.data.namespaces)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load namespaces";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Namespaces",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, [logFilter.environments])

    // Get log attributes for LogSearch component
    useEffect(() => {
        axios.get("/api/v1/logsSummaryAttributes").then((response) => {
            let attributes = response.data.attributes;
            let newMap = new Map<string, Attribute[]>();
            for (let attribute of attributes) {
                if (disallowedKeys.includes(attribute)) {
                    continue; // Skip disallowed keys
                }
                newMap.set(attribute, []);
            }
            setLogAttributes(newMap);
        }).catch((e) => {
            console.error(e);
        });
    }, []);

    // Sync LogSearch state with customFilterArgs
    useEffect(() => {
        if (logFilter.filterType === FilterType.Custom) {
            setLogFilter(prev => {
                return {
                    ...prev,
                    customFilterArgs: {
                        filters: filter,
                        excludeFilters: excludeFilter,
                        regexes: regexes,
                        excludeRegexes: excludeRegexes
                    }
                };
            });
        }
    }, [filter, excludeFilter, regexes, excludeRegexes]);

    // Initialize LogSearch state from customFilterArgs when filter type changes to Custom
    useEffect(() => {
        if (logFilter.filterType === FilterType.Custom && logFilter.customFilterArgs) {
            // Initialize filter from customFilterArgs.filters
            if (logFilter.customFilterArgs.filters) {
                const newFilter = new Map<string, string[]>();

                // If it's a Map, iterate through it directly
                if (logFilter.customFilterArgs.filters instanceof Map) {
                    logFilter.customFilterArgs.filters.forEach((values, key) => {
                        newFilter.set(key, values);
                    });
                } else {
                    // If it's a plain object (from API response), convert to Map
                    // Need to use type assertion since the API response has plain objects
                    Object.entries(logFilter.customFilterArgs.filters as any).forEach(([key, values]) => {
                        newFilter.set(key, values as string[]);
                    });
                }

                setFilter(newFilter);
            }

            // Initialize excludeFilter from customFilterArgs.excludeFilters
            if (logFilter.customFilterArgs.excludeFilters) {
                const newExcludeFilter = new Map<string, string[]>();

                // If it's a Map, iterate through it directly
                if (logFilter.customFilterArgs.excludeFilters instanceof Map) {
                    logFilter.customFilterArgs.excludeFilters.forEach((values, key) => {
                        newExcludeFilter.set(key, values);
                    });
                } else {
                    // If it's a plain object (from API response), convert to Map
                    // Need to use type assertion since the API response has plain objects
                    Object.entries(logFilter.customFilterArgs.excludeFilters as any).forEach(([key, values]) => {
                        newExcludeFilter.set(key, values as string[]);
                    });
                }

                setExcludeFilter(newExcludeFilter);
            }

            // Initialize regexes and excludeRegexes
            if (logFilter.customFilterArgs.regexes) {
                setRegexes(logFilter.customFilterArgs.regexes);
            }

            if (logFilter.customFilterArgs.excludeRegexes) {
                setExcludeRegexes(logFilter.customFilterArgs.excludeRegexes);
            }
        }
    }, [logFilter.filterType]);


    return (
        <Dialog open={props.openAddModal} modal={false} >
            <DialogContent
                ref={dialogRef}
                className={"w-screen h-screen m-0 p-0 bg-black/75 flex items-center justify-center border-none"}
                onInteractOutside={() => props.setOpenAddModal(false)}>
                <div className={"w-1/2 p-4 bg-background flex flex-col gap-4 text-textmedium border border-border rounded"}>
                    <h3 className={"text-lg font-bold"}>Add a log filter</h3>
                    {/* Button group for inclusion and exclusion*/}
                    <p>
                        1. Select whether you want to include or exclude log lines that match the filter pattern.
                    </p>
                    <div className={"flex gap-2"}>
                        <Button
                            className={
                                cn("border rounded", logFilter.inclusionType === IncludeOrExclude.Exclude ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setLogFilter((prev) => {
                                prev.inclusionType = IncludeOrExclude.Exclude
                                return {...prev}
                            })}>
                            Exclude</Button>
                        <Button
                            className={
                                cn("border rounded", logFilter.inclusionType === IncludeOrExclude.Include ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setLogFilter((prev) => {
                                prev.inclusionType = IncludeOrExclude.Include
                                return {...prev}
                            })}>
                            Include</Button>
                    </div>
                    <p>
                        2. Select the environments that you want this filter to apply to:
                    </p>
                    <div className={"flex h-full"}>
                        <MultiSelectorDropDown
                            selectorSingular={"Environment"}
                            possibleItems={environments}
                            selectedItems={stringArrayToDropDownItemArray(logFilter.environments)}
                            setSelectedItems={(selectedItems) => {
                                setLogFilter((prev) => {
                                    prev.environments = dropDownItemArrayToStringArray(selectedItems)
                                    return {...prev}
                                })
                            }}
                            visualSettings={{
                                dropdownAlign: "start"
                            }}
                        />
                    </div>
                    <p>
                        3. Select whether you want to match all services in a set of namespaces or a specific set of
                        services.
                    </p>
                    <div className={"flex gap-2"}>
                        <Button
                            className={
                                cn("border rounded", logFilter.filterType === FilterType.Service ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setLogFilter((prev) => {
                                if (prev.filterType === FilterType.Service) {
                                    return prev
                                }

                                prev.filterType = FilterType.Service
                                prev.serviceFilterArgs = {
                                    services: [],
                                    regex: prev.namespaceFilterArgs?.regex || ".*"
                                }
                                prev.namespaceFilterArgs = undefined
                                prev.customFilterArgs = undefined
                                return {...prev}
                            })}>
                            Services</Button>
                        <Button
                            className={
                                cn("border rounded", logFilter.filterType === FilterType.Namespace ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setLogFilter((prev) => {
                                if (prev.filterType === FilterType.Namespace) {
                                    return prev
                                }
                                prev.filterType = FilterType.Namespace
                                prev.namespaceFilterArgs = {
                                    namespaces: [],
                                    regex: prev.serviceFilterArgs?.regex || ".*"
                                }
                                prev.serviceFilterArgs = undefined
                                prev.customFilterArgs = undefined
                                return {...prev}
                            })}>
                            Namespaces</Button>
                        <Button
                            className={
                                cn("border rounded", logFilter.filterType === FilterType.Custom ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setLogFilter((prev) => {
                                if (prev.filterType === FilterType.Custom) {
                                    return prev
                                }
                                prev.filterType = FilterType.Custom
                                prev.customFilterArgs = {
                                    filters: new Map(),
                                    excludeFilters: new Map(),
                                    regexes: [],
                                    excludeRegexes: []
                                }
                                prev.serviceFilterArgs = undefined
                                prev.namespaceFilterArgs = undefined
                                return {...prev}
                            })}>
                            Custom</Button>
                    </div>
                    <div className={"text-textmedium"}>
                        <div>
                            {logFilter.filterType === FilterType.Custom ? (
                                "4. Configure your custom filter:"
                            ) : (
                                `4. Select the ${logFilter.filterType === FilterType.Service ? "services" : "namespaces"} that
                                you want this rule to be applied to:`
                            )}
                        </div>
                    </div>
                    {
                        logFilter.filterType === FilterType.Service &&
                        <div className={"flex h-full"}>
                            <MultiSelectorDropDown
                                selectorSingular={"Service"}
                                possibleItems={
                                    services.map((service) => {
                                        return {
                                            displayName: service.displayName,
                                            value: service.serviceName
                                        }
                                    })
                                }
                                selectedItems={stringArrayToDropDownItemArray(logFilter.serviceFilterArgs?.services || [])}
                                setSelectedItems={(selectedItems) => {
                                    setLogFilter((prev) => {
                                        prev.serviceFilterArgs!.services = dropDownItemArrayToStringArray(selectedItems)
                                        return {...prev}
                                    })
                                }}
                                visualSettings={{
                                    dropdownAlign: "start"
                                }}
                            />

                        </div>
                    }
                    {
                        logFilter.filterType === FilterType.Namespace &&
                        <div className={"flex h-full"}>
                            <MultiSelectorDropDown
                                selectorSingular={"Namespace"}
                                possibleItems={stringArrayToDropDownItemArray(namespaces)}
                                selectedItems={stringArrayToDropDownItemArray(logFilter.namespaceFilterArgs?.namespaces || [])}
                                setSelectedItems={(selectedItems) => {
                                    setLogFilter((prev) => {
                                        prev.namespaceFilterArgs!.namespaces = dropDownItemArrayToStringArray(selectedItems)
                                        return {...prev}
                                    })
                                }}
                                visualSettings={{
                                    dropdownAlign: "start"
                                }}
                            />
                        </div>
                    }
                    {
                        logFilter.filterType === FilterType.Custom &&
                        <div className="flex flex-col w-full gap-4">
                            <div className="min-w-0 flex flex-col grow shrink min-h-0 overflow-y-auto">
                                    <LogSearch
                                        hideClearFiltersButton={true}
                                        filter={filter}
                                        setFilter={setFilter}
                                        excludeFilter={excludeFilter}
                                        setExcludeFilter={setExcludeFilter}
                                        regexes={regexes}
                                        setRegexes={setRegexes}
                                        excludeRegexes={excludeRegexes}
                                        setExcludeRegexes={setExcludeRegexes}
                                        logAttributes={logAttributes}
                                        dialogRef={dialogRef}
                                        isMetoroQLMode={false}
                                        transforms={transforms}
                                        setTransforms={setTransforms}
                                        customColumns={customColumns}
                                        setCustomColumns={setCustomColumns}
                                        setFreeTextFilter={() => {}}
                                        disallowKeys={disallowedKeys}
                                        dropDownFunction={(key: string) => {
                                            let filters = Object.fromEntries(filter);
                                            let excludeFilters = Object.fromEntries(excludeFilter);
                                            const request = {
                                                "startTime": Math.floor(Date.now() / 1000 - 300), // Last 5 minutes hour
                                                "endTime": Math.floor(Date.now() / 1000),
                                                "excludeFilters": excludeFilters,
                                                "filters": filters,
                                                "regexes": regexes,
                                                "excludeRegexes": excludeRegexes,
                                                "environments": logFilter.environments,
                                                "attribute": key,
                                                "transforms": transforms,
                                            }

                                            return axios.post("/api/v1/logsSummaryIndividualAttribute", request).then((response) => {
                                                return response.data.attribute;
                                            })
                                        }}
                                    />
                                </div>
                        </div>
                    }

                    {logFilter.filterType !== FilterType.Custom && (
                        <>
                            <p className={"text-sm"}>5. {logFilter.inclusionType === IncludeOrExclude.Include
                                ? "Enter the inclusion pattern to select log lines for ingestion. Only logs matching this pattern will be sent out of your cluster."
                                : "Enter the exclusion pattern to prevent matching log lines from being sent out of your cluster for ingestion."
                            }</p>
                            <Input
                                value={logFilter.filterType == FilterType.Namespace ? logFilter.namespaceFilterArgs?.regex : logFilter.serviceFilterArgs?.regex}
                                onChange={(e) => {
                                    if (logFilter.filterType == FilterType.Namespace) {
                                        setLogFilter((prev) => {
                                            prev.namespaceFilterArgs!.regex = e.target.value
                                            return {...prev}
                                        })
                                    }
                                    if (logFilter.filterType == FilterType.Service) {
                                        setLogFilter((prev) => {
                                            prev.serviceFilterArgs!.regex = e.target.value
                                            return {...prev}
                                        })
                                    }
                                }}
                                type={"text"} placeholder={"Exclusion regex pattern"}
                                className={"border border-primary rounded p-2"}/>
                        </>
                    )}
                    <div className={"flex justify-end gap-4"}>
                        <Button
                            onClick={() => props.setOpenAddModal(false)}
                            className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Cancel</Button>
                        <Button
                            onClick={() => {
                                // Convert Map objects to regular objects for JSON serialization
                                const logFilterToSend = {...logFilter};

                                // If it's a custom filter, convert the Maps to regular objects for JSON serialization
                                if (logFilterToSend.filterType === FilterType.Custom && logFilterToSend.customFilterArgs) {
                                    const customFilterArgs = {...logFilterToSend.customFilterArgs};

                                    // Convert filters Map to regular object
                                    const filtersObj: {[key: string]: string[]} = {};
                                    customFilterArgs.filters.forEach((value, key) => {
                                        filtersObj[key] = value;
                                    });

                                    // Convert excludeFilters Map to regular object
                                    const excludeFiltersObj: {[key: string]: string[]} = {};
                                    customFilterArgs.excludeFilters.forEach((value, key) => {
                                        excludeFiltersObj[key] = value;
                                    });

                                    // Replace the Maps with plain objects for JSON serialization
                                    // Need to use type assertion since the backend expects plain objects
                                    logFilterToSend.customFilterArgs = {
                                        ...customFilterArgs,
                                        filters: filtersObj,
                                        excludeFilters: excludeFiltersObj
                                    } as any;
                                }

                                axios.post("/api/v1/logFilter", logFilterToSend)
                                .then(() => {
                                    props.setTriggerReload()
                                    props.setOpenAddModal(false)
                                }).catch((e) => {
                                    console.error(e)
                                })
                            }}
                            className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded"}>Save</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export function TraceExclusionSettings() {
    const [traceFilters, setTraceFilters] = React.useState<any[]>([]);

    // Delete modal
    const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean[]>([])
    const [triggerReload, setTriggerReload] = React.useState(false)
    const [openAddModal, setOpenAddModal] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(true)

    // Column definitions for the DataTable
    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'inclusionType',
            header: "Type",
            cell: ({ row }) => {
                return <div className="py-2 whitespace-nowrap">{row.original.inclusionType || "Unknown"}</div>
            },
        },
        {
            accessorKey: 'entities',
            header: "Entities",
            cell: ({ row }) => {
                const filter = row.original;
                return (
                    <div className="py-2">
                        <div>
                            {filter.customTraceFilterArgs?.filters && Object.entries(filter.customTraceFilterArgs.filters).length > 0 ? (
                                <div>
                                    {Object.entries(filter.customTraceFilterArgs.filters).map(([key, values], i) => (
                                        <div key={i} className="leading-6">
                                            {key} = {Array.isArray(values) ? (values as string[]).join('  || ') : String(values)}
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {filter.customTraceFilterArgs?.excludeFilters && Object.entries(filter.customTraceFilterArgs.excludeFilters).length > 0 ? (
                                <div className="mt-2">
                                    {Object.entries(filter.customTraceFilterArgs.excludeFilters).map(([key, values], i) => (
                                        <div key={i} className="leading-6">
                                            {key} ≠ {Array.isArray(values) ? (values as string[]).join(' || ') : String(values)}
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {(!filter.customTraceFilterArgs?.filters || Object.entries(filter.customTraceFilterArgs.filters).length === 0) && 
                             (!filter.customTraceFilterArgs?.excludeFilters || Object.entries(filter.customTraceFilterArgs.excludeFilters).length === 0) && 
                             "-"}
                        </div>
                    </div>
                )
            },
        },
        {
            id: 'actions',
            header: "",
            cell: ({ row }) => {
                const filter = row.original;
                const index = traceFilters.findIndex(f => f.id === filter.id);
                return (
                    <div className="flex-none flex flex-col justify-center">
                        <Dialog open={openDeleteModal[index]}>
                            <DialogContent className={"w-1/3"}>
                                <div className={"flex flex-col gap-4 text-textmedium"}>
                                    <h3 className={"text-lg font-bold"}>Remove filter</h3>
                                    <div className={"text-textmedium"}>Are you sure you want to
                                        remove filter ?
                                    </div>
                                    <div className={"flex justify-end gap-4"}>
                                        <Button
                                            onClick={() => setOpenDeleteModal(prev => {
                                                prev[index] = false
                                                return [...prev]
                                            })}
                                            className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Cancel</Button>
                                        <Button
                                            onClick={() => {
                                                axios.delete("/api/v1/traceFilter?id=" + filter.id).then(() => {
                                                    setTriggerReload(prev => !prev)
                                                }).catch(e => console.error(e))
                                                setOpenDeleteModal(prev => {
                                                    prev[index] = false
                                                    return [...prev]
                                                })
                                            }}
                                            className={"bg-red-500/20 border border-red-500 text-textmedium hover:text-textlight rounded"}>Remove</Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Button
                            requiredPermission={{
                                operation: "delete",
                                resource: "traceRedacts/default/*"
                            }}
                            onClick={() => {
                                setOpenDeleteModal(prev => {
                                    prev[index] = true
                                    return [...prev]
                                })
                            }}
                            className={"bg-red-500/20 h-[32px] border border-red-500 text-textmedium hover:text-textlight rounded w-max"}>Remove</Button>
                    </div>
                )
            },
        },
    ];

    useEffect(() => {
        setIsLoading(true);
        axios.get("/api/v1/traceFilters").then((response) => {
            setTraceFilters(response.data.traceFilters)
            setOpenDeleteModal(new Array(response.data.traceFilters.length).fill(false))
            setIsLoading(false);
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load trace filters";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Trace Filters",
                description: errorMessage,
                variant: "destructive"
            });
            setIsLoading(false);
        })
    }, [triggerReload])

    return (
        <div className={""}>
            <div className={"flex justify-between"}>
                <div>
                    <h2 className={"text-lg font-bold text-textmedium"}>Trace Filters</h2>
                    <p className={"text-sm text-textdark"}>
                        Add filters to prevent traces from being exported from your cluster. This helps protect sensitive
                        data and manage trace collection.{" "}
                        <a
                            href="https://metoro.io/docs/traces/ingestion-settings"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        >
                            Learn more
                            <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                        </a>
                    </p>
                </div>
                <div className={"flex flex-col justify-center"}>
                    <NewTraceFilterModal
                        openAddModal={openAddModal}
                        setOpenAddModal={setOpenAddModal}
                        setTraceFilters={setTraceFilters}
                        setTriggerReload={() => {
                            setTriggerReload(prev => !prev)
                        }}
                    />
                    <Button
                        requiredPermission={{
                            resource: "traceRedacts/default/*",
                            operation: "create"
                        }}
                        onClick={() => setOpenAddModal(true)}
                        className={"bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded"}>Add
                        Filter</Button>
                </div>
            </div>
            <div className="mt-4">
                <DataTable
                    columns={columns}
                    data={traceFilters}
                    tableTitle="Trace Filters"
                    filters={[]}
                    isLoading={isLoading}
                    emptyStateMessage="Add a trace filter to prevent Metoro from exporting traces that match specific conditions."
                    className="bg-backgroundmedium"
                />
            </div>
        </div>
    );
}

function NewTraceFilterModal(
    props: {
        openAddModal: boolean,
        setOpenAddModal: (open: boolean) => void,
        setTraceFilters: (traceFilters: any[]) => void,
        setTriggerReload: () => void
    }
) {
    // State for LogsComponent (reused for trace filtering)
    const [filter, setFilter] = useState<Map<string, string[]>>(new Map())
    const [excludeFilter, setExcludeFilter] = useState<Map<string, string[]>>(new Map())
    const [regexes, setRegexes] = useState<string[]>([])
    const [excludeRegexes, setExcludeRegexes] = useState<string[]>([])
    const [traceAttributes, setTraceAttributes] = useState<Map<string, Attribute[]>>(new Map())
    const [customColumns, setCustomColumns] = useState<string[]>([])
    const [transforms, setTransforms] = useState<Transform[]>([])

    const dialogRef = React.useRef<HTMLDivElement>(null);
    const [traceFilter, setTraceFilter] = React.useState<TraceFilter>({
        id: "",
        inclusionType: IncludeOrExclude.Exclude,
        customTraceFilterArgs: {
            filters: {},
            excludeFilters: {}
        }
    })

    // Get trace attributes for LogSearch component (reused for trace filtering)
    useEffect(() => {
        axios.get("/api/v1/tracesSummaryAttributes").then((response) => {
            let attributes = response.data.attributes;
            let newMap = new Map<string, Attribute[]>();
            for (let attribute of attributes) {
                newMap.set(attribute, []);
            }
            setTraceAttributes(newMap);
        }).catch((e) => {
            console.error(e);
        });
    }, []);

    // Sync LogSearch state with customTraceFilterArgs
    useEffect(() => {
        setTraceFilter((prev: TraceFilter) => {
            return {
                ...prev,
                customTraceFilterArgs: {
                    filters: Object.fromEntries(filter),
                    excludeFilters: Object.fromEntries(excludeFilter)
                }
            };
        });
    }, [filter, excludeFilter]);

    return (
        <Dialog open={props.openAddModal} modal={false}>
            <DialogContent
                ref={dialogRef}
                className={"w-screen h-screen m-0 p-0 bg-black/75 flex items-center justify-center border-none"}                onInteractOutside={() => props.setOpenAddModal(false)}>
                <div className={"w-1/2 flex flex-col gap-4 text-textmedium border border-border rounded p-4 bg-background"}>
                    <h3 className={"text-lg font-bold"}>Add a trace filter</h3>
                    {/* Button group for inclusion and exclusion*/}
                    <p>
                        1. Select whether you want to include or exclude traces that match the filter pattern.
                    </p>
                    <div className={"flex gap-2"}>
                        <Button
                            className={
                                cn("border rounded", traceFilter.inclusionType === IncludeOrExclude.Exclude ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setTraceFilter((prev: TraceFilter) => {
                                prev.inclusionType = IncludeOrExclude.Exclude
                                return {...prev}
                            })}>
                            Exclude</Button>
                        <Button
                            className={
                                cn("border rounded", traceFilter.inclusionType === IncludeOrExclude.Include ? "bg-secondarytransparenter border-secondary" : "border-border bg-background",)
                            }
                            onClick={() => setTraceFilter((prev: TraceFilter) => {
                                prev.inclusionType = IncludeOrExclude.Include
                                return {...prev}
                            })}>
                            Include</Button>
                    </div>
                    <p>
                        2. Configure your filter:
                    </p>
                    <div className="flex flex-col w-full gap-4">
                        <div className="min-w-0 flex flex-col grow shrink min-h-0 overflow-y-auto">
                            <LogSearch
                                hideClearFiltersButton={true}
                                filter={filter}
                                setFilter={setFilter}
                                excludeFilter={excludeFilter}
                                setExcludeFilter={setExcludeFilter}
                                regexes={regexes}
                                setRegexes={setRegexes}
                                excludeRegexes={excludeRegexes}
                                setExcludeRegexes={setExcludeRegexes}
                                logAttributes={traceAttributes}
                                dialogRef={dialogRef}
                                isMetoroQLMode={false}
                                transforms={transforms}
                                setTransforms={setTransforms}
                                customColumns={customColumns}
                                setCustomColumns={setCustomColumns}
                                setFreeTextFilter={() => {}}
                                dropDownFunction={(key: string) => {
                                    let filters = Object.fromEntries(filter);
                                    let excludeFilters = Object.fromEntries(excludeFilter);
                                    const request = {
                                        "startTime": Math.floor(Date.now() / 1000 - 300), // Last 5 minutes
                                        "endTime": Math.floor(Date.now() / 1000),
                                        "excludeFilters": excludeFilters,
                                        "filters": filters,
                                        "regexes": regexes,
                                        "excludeRegexes": excludeRegexes,
                                        "attribute": key,
                                        "transforms": transforms,
                                    }

                                    return axios.post("/api/v1/tracesSummaryIndividualAttribute", request).then((response) => {
                                        return response.data.attribute;
                                    })
                                }}
                            />
                        </div>
                    </div>
                    <div className={"flex justify-end gap-4"}>
                        <Button
                            onClick={() => props.setOpenAddModal(false)}
                            className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Cancel</Button>
                        <Button
                            onClick={() => {
                                axios.post("/api/v1/traceFilter", traceFilter)
                                .then(() => {
                                    props.setTriggerReload()
                                    props.setOpenAddModal(false)
                                }).catch((e) => {
                                    console.error(e)
                                })
                            }}
                            className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded"}>Save</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export function TraceRedactionSettings() {
    const [traceRedacts, setTraceRedacts] = React.useState<TraceRedact[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean[]>([]);
    const [triggerReload, setTriggerReload] = React.useState(false);
    const [openAddModal, setOpenAddModal] = React.useState(false);

    useEffect(() => {
        axios.get("/api/v1/traceRedacts")
            .then((response) => {
                setTraceRedacts(response.data.traceRedacts);
                setOpenDeleteModal(new Array(response.data.traceRedacts.length).fill(false));
            })
            .catch((e) => {
                console.error("Failed to fetch trace redaction rules:", e);
                let errorMessage = "Failed to fetch trace redaction rules";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    title: "Failed to Load Trace Redaction Rules and Log Filters",
                    description: errorMessage,
                    variant: "destructive"
                });
            });
    }, [triggerReload]);


    const handleDelete = async (uuid: string) => {
        try {
            await axios.delete(`/api/v1/traceRedact?uuid=${uuid}`);
            setTriggerReload(prev => !prev);
            toast({
                title: "Success",
                description: "Trace redaction rule deleted successfully"
            });
        } catch (error) {
            console.error("Failed to delete trace redaction rule:", error);
            let errorMessage = "Failed to delete trace redaction rule";
            // @ts-ignore
            if (error.response && error.response.status === 403) {
                // @ts-ignore
                errorMessage = error.response.data.error;
            }
            toast({
                title: "Failed to Delete Trace Redaction Rule",
                description: errorMessage,
                variant: "destructive"
            });
        }
    };

    return (
        <div className="m-4">
            <div className="flex justify-between">
                <div>
                    <h2 className="text-lg font-bold text-textmedium">Trace Redaction Rules</h2>
                    <p className="text-sm text-textdark">
                        Configure rules to redact sensitive information from the http.url and http.path attributes in
                        your traces.{" "}
                        <a
                            href="https://metoro.io/docs/traces/redaction"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        >
                            Learn more
                            <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                        </a>
                    </p>
                </div>
                <Button
                    requiredPermission={{
                        resource: "traceRedacts/default/*",
                        operation: "create"
                    }}
                    onClick={() => setOpenAddModal(true)}
                    className="bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded"
                >
                    Add Rule
                </Button>
            </div>

            {traceRedacts.length === 0 ? (
                <div className="w-full flex-none mt-4 p-4 rounded bg-backgroundlight border flex grow shrink">
                    Add a trace redaction rule to redact sensitive information from your traces.
                </div>
            ) : (
                <div className="border-l border-r border-t mt-4 rounded-t bg-backgroundlight">
                    <div
                        className="w-full flex-none h-[48px] px-4 py-2 rounded-tl rounded-tr justify-start items-start gap-4 flex grow shrink">
                        <div className="w-[360px] h-full font-normal font-['Inter'] leading-8 text-textmedium text-xl">
                            Environments
                        </div>
                        <div className="w-[360px] h-full font-normal font-['Inter'] leading-8 text-textmedium text-xl">
                            Services
                        </div>
                        <div
                            className="flex grow shrink h-full font-normal font-['Inter'] leading-8 text-textmedium text-xl">
                            Pattern → Replacement
                        </div>
                    </div>

                    <div className="bg-backgroundlight">
                        {traceRedacts.map((redact, index) => (
                            <div
                                key={redact.uuid}
                                className={cn(
                                    "w-full flex-none min-h-[48px] px-4 py-2 border-t justify-start items-start gap-4 flex grow shrink",
                                    index === traceRedacts.length - 1 ? "border-b" : ""
                                )}
                            >
                                <div className="w-[360px] font-normal font-['Inter'] py-2 text-textmedium text-md">
                                    {redact.environments && redact.environments.length > 0
                                        ? redact.environments.map((env, i) => (
                                            <div key={i} className="leading-6">{env}</div>
                                        ))
                                        : "All"}
                                </div>
                                <div className="w-[360px] font-normal font-['Inter'] py-2 text-textmedium text-md">
                                    {redact.serviceNames && redact.serviceNames.length > 0
                                        ? redact.serviceNames.map((service, i) => (
                                            <div key={i} className="leading-6">{service}</div>
                                        ))
                                        : "All"}
                                </div>
                                <div
                                    className="flex grow shrink font-normal font-['Inter'] py-2 text-textmedium text-md">
                                    <div className="truncate">{redact.pattern}</div>
                                    <div className="text-textdark mx-2">→</div>
                                    <div className="truncate">{redact.replacement}</div>
                                </div>
                                <div className="flex flex-col justify-center">
                                    <Dialog open={openDeleteModal[index]}>
                                        <DialogContent className="w-1/3">
                                            <div className="flex flex-col gap-4 text-textmedium">
                                                <h3 className="text-lg font-bold">Remove redaction rule</h3>
                                                <div className="text-textmedium">
                                                    Are you sure you want to remove this redaction rule?
                                                </div>
                                                <div className="flex justify-end gap-4">
                                                    <Button
                                                        onClick={() => {
                                                            const newOpenDeleteModal = [...openDeleteModal];
                                                            newOpenDeleteModal[index] = false;
                                                            setOpenDeleteModal(newOpenDeleteModal);
                                                        }}
                                                        className="bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded"
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button

                                                        onClick={() => {
                                                            handleDelete(redact.uuid);
                                                            const newOpenDeleteModal = [...openDeleteModal];
                                                            newOpenDeleteModal[index] = false;
                                                            setOpenDeleteModal(newOpenDeleteModal);
                                                        }}
                                                        className="bg-red-500/20 border border-red-500 text-textmedium hover:text-textlight rounded"
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                    <Button
                                        requiredPermission={{
                                            operation: "delete",
                                            resource: "traceRedacts"
                                        }}
                                        onClick={() => {
                                            const newOpenDeleteModal = [...openDeleteModal];
                                            newOpenDeleteModal[index] = true;
                                            setOpenDeleteModal(newOpenDeleteModal);
                                        }}
                                        className="bg-red-500/20 h-[32px] border border-red-500 text-textmedium hover:text-textlight rounded"
                                    >
                                        Remove
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <NewTraceRedactionModal
                openAddModal={openAddModal}
                setOpenAddModal={setOpenAddModal}
                setRedacts={setTraceRedacts}
                setTriggerReload={() => setTriggerReload(prev => !prev)}
            />
        </div>
    );
}

interface RetentionSettings {
    metricsTTLSeconds: number;
    logsTTLSeconds: number;
    tracesTTLSeconds: number;
    profilingTTLSeconds: number;
    kubernetesResourcesTTLSeconds: number;
}

function RetentionSettings() {
    const [retentionSettings, setRetentionSettings] = React.useState<RetentionSettings | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setIsLoading(true);
        axios.get("/api/v1/account/retention")
            .then((response) => {
                setRetentionSettings(response.data);
                setError(null);
            })
            .catch((e) => {
                console.error("Failed to fetch retention settings:", e);
                setError("Failed to load retention settings. Please try again later.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // Helper function to convert seconds to days
    const secondsToDays = (seconds: number) => {
        return seconds / (60 * 60 * 24);
    };

    return (
        <div className={"p-4"}>
            <div className={"flex flex-col gap-2"}>
                <h3 className={"text-lg font-semibold"}>Data Retention Settings</h3>
                <div className={"text-textmedium mb-4"}>
                    <p>These settings control how long your data is stored in Metoro. Contact support if you need to change these values.</p>
                </div>

                {isLoading && <LoadingSpinner className={"mx-auto my-4"} />}

                {error && (
                    <div className={"text-red-500 mb-4"}>
                        {error}
                    </div>
                )}

                {retentionSettings && (
                    <div className={"grid grid-cols-1 md:grid-cols-2 gap-4"}>
                        <div className={"flex flex-col gap-2"}>
                            <label className={"text-sm font-medium"}>Metrics Retention</label>
                            <div className={"flex items-center gap-2"}>
                                <Input 
                                    value={`${secondsToDays(retentionSettings.metricsTTLSeconds)} days`} 
                                    disabled 
                                    className={"bg-backgrounddark"}
                                />
                            </div>
                        </div>

                        <div className={"flex flex-col gap-2"}>
                            <label className={"text-sm font-medium"}>Logs Retention</label>
                            <div className={"flex items-center gap-2"}>
                                <Input 
                                    value={`${secondsToDays(retentionSettings.logsTTLSeconds)} days`} 
                                    disabled 
                                    className={"bg-backgrounddark"}
                                />
                            </div>
                        </div>

                        <div className={"flex flex-col gap-2"}>
                            <label className={"text-sm font-medium"}>Traces Retention</label>
                            <div className={"flex items-center gap-2"}>
                                <Input 
                                    value={`${secondsToDays(retentionSettings.tracesTTLSeconds)} days`} 
                                    disabled 
                                    className={"bg-backgrounddark"}
                                />
                            </div>
                        </div>

                        <div className={"flex flex-col gap-2"}>
                            <label className={"text-sm font-medium"}>Profiling Retention</label>
                            <div className={"flex items-center gap-2"}>
                                <Input 
                                    value={`${secondsToDays(retentionSettings.profilingTTLSeconds)} days`} 
                                    disabled 
                                    className={"bg-backgrounddark"}
                                />
                            </div>
                        </div>

                        <div className={"flex flex-col gap-2"}>
                            <label className={"text-sm font-medium"}>Kubernetes Resources Retention</label>
                            <div className={"flex items-center gap-2"}>
                                <Input 
                                    value={`${secondsToDays(retentionSettings.kubernetesResourcesTTLSeconds)} days`} 
                                    disabled 
                                    className={"bg-backgrounddark"}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className={"mt-4 p-4 bg-blue-500/10 border border-blue-500 rounded-md"}>
                    <p className={"text-sm"}>
                        Need to adjust your retention settings? Please contact our support team at <a href="mailto:support@metoro.io" className={"text-primary hover:underline"}>support@metoro.io</a> for assistance.
                    </p>
                </div>
            </div>
        </div>
    );
}

export function NewTraceRedactionModal(
    props: {
        openAddModal: boolean,
        setOpenAddModal: (open: boolean) => void,
        setRedacts: (redacts: TraceRedact[]) => void,
        setTriggerReload: () => void
    }
) {
    const [services, setServices] = React.useState<KnownService[]>([]);
    const [environments, setEnvironments] = React.useState<DropDownItem[]>([]);
    const [showRegexTester, setShowRegexTester] = React.useState(false);

    const [traceRedact, setTraceRedact] = React.useState<{
        environments: string[],
        serviceNames: string[],
        pattern: string,
        replacement: string
    }>({
        environments: [],
        serviceNames: [],
        pattern: "",
        replacement: ""
    });

    const [testString, setTestString] = React.useState("Example: password=secret123&token=abc123");
    const [redactedOutput, setRedactedOutput] = React.useState("");
    const [isValidRegex, setIsValidRegex] = React.useState(true);

    // Fetch environments
    useEffect(() => {
        axios.get("/api/v1/environments").then((response) => {
            setEnvironments(stringArrayToDropDownItemArray(response.data.environments))
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load environments";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Environments",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, []);

    // Fetch services when environments change
    useEffect(() => {
        // Fetch environments
        axios.post("/api/v1/serviceSummaries", {
            environments: traceRedact.environments,
            startTime: Math.floor(Date.now() / 1000 - 300),
            endTime: Math.floor(Date.now() / 1000),
        }).then((response) => {
            setServices(response.data.serviceSummaries)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to load services";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Load Services",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, [traceRedact.environments]);

    // Update redacted output whenever pattern, replacement, or test string changes
    useEffect(() => {
        try {
            if (!traceRedact.pattern) {
                setRedactedOutput(testString);
                setIsValidRegex(true);
                return;
            }

            const regex = new RegExp(traceRedact.pattern, 'g');
            setIsValidRegex(true);
            setRedactedOutput(testString.replace(regex, traceRedact.replacement || '$1'));
        } catch (error) {
            setIsValidRegex(false);
            setRedactedOutput(testString);
        }
    }, [testString, traceRedact.pattern, traceRedact.replacement]);

    const handleSave = async () => {
        try {
            const response = await axios.post("/api/v1/traceRedact", {
                environments: traceRedact.environments,
                serviceNames: traceRedact.serviceNames,
                pattern: traceRedact.pattern,
                replacement: traceRedact.replacement
            });
            props.setTriggerReload();
            props.setOpenAddModal(false);
            toast({
                title: "Success",
                description: "Trace redaction rule created successfully"
            });
        } catch (error) {
            console.error("Failed to create trace redaction rule:", error);
            let errorMessage = "Failed to create trace redaction rule";
            // @ts-ignore
            if (error.response && error.response.status === 403) {
                // @ts-ignore
                errorMessage = error.response.data.error;
            }
            toast({
                title: "Failed to Create Trace Redaction Rule",
                description: errorMessage,
                variant: "destructive"
            });
        }
    };

    return (
        <Dialog open={props.openAddModal}>
            <DialogContent
                className={cn("bg-background", showRegexTester ? "w-[80%] max-w-[1400px]" : "w-1/2")}
                onInteractOutside={() => props.setOpenAddModal(false)}
            >
                <div className="flex gap-8">
                    <div className="flex-1 flex flex-col gap-4 text-textmedium">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-bold">Add a trace redaction rule</h3>
                            <Button
                                onClick={() => setShowRegexTester(prev => !prev)}
                                className="bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded"
                            >
                                {showRegexTester ? "Hide Regex Tester" : "Test Regex"}
                            </Button>
                        </div>

                        <p>1. Select the environments that you want this redaction rule to apply to:</p>
                        <div className={"flex h-full w-full"}>
                            <MultiSelectorDropDown
                                selectorSingular={"Environment"}
                                possibleItems={environments}
                                selectedItems={stringArrayToDropDownItemArray(traceRedact.environments)}
                                setSelectedItems={(selectedItems) => {
                                    setTraceRedact(prev => ({
                                        ...prev,
                                        environments: dropDownItemArrayToStringArray(selectedItems)
                                    }))
                                }}
                                visualSettings={{
                                    dropdownAlign: "start"
                                }}
                            />
                        </div>
                        <p className={"text-xs text-textdark"}>
                            Leave empty to apply to all environments
                        </p>

                        <p>2. Select the services that you want this redaction rule to apply to:</p>
                        <div className={"flex w-full h-full"}>
                            <MultiSelectorDropDown
                                selectorSingular={"Service"}
                                possibleItems={
                                    services.map((service) => ({
                                        displayName: service.displayName,
                                        value: service.serviceName
                                    }))
                                }
                                selectedItems={stringArrayToDropDownItemArray(traceRedact.serviceNames)}
                                setSelectedItems={(selectedItems) => {
                                    setTraceRedact(prev => ({
                                        ...prev,
                                        serviceNames: dropDownItemArrayToStringArray(selectedItems)
                                    }))
                                }}
                                visualSettings={{
                                    dropdownAlign: "start"
                                }}

                            />
                        </div>
                        <p className={"text-xs text-textdark"}>
                            Leave empty to apply to all services
                        </p>

                        <p className={"text-sm"}>3. Enter the regex pattern to match sensitive information:</p>
                        <Input
                            value={traceRedact.pattern}
                            onChange={(e) => setTraceRedact(prev => ({...prev, pattern: e.target.value}))}
                            type={"text"}
                            placeholder={"e.g., password=([^&\\s]+)"}
                            className={cn(
                                "border border-primary rounded p-2",
                                !isValidRegex && "border-red-500"
                            )}
                        />
                        {!isValidRegex && (
                            <p className="text-sm text-red-500">Invalid regular expression</p>
                        )}
                        <p className={"text-xs text-textdark"}>
                            Use regex capture groups to define what should be redacted. Example: password=([^&\s]+) will
                            match "password=secret123"
                        </p>

                        <p className={"text-sm"}>4. Enter the replacement text:</p>
                        <Input
                            value={traceRedact.replacement}
                            onChange={(e) => setTraceRedact(prev => ({...prev, replacement: e.target.value}))}
                            type={"text"}
                            placeholder={"e.g., password=REDACTED"}
                            className={"border border-primary rounded p-2"}
                        />

                        <div className={"flex justify-end gap-4"}>
                            <Button
                                onClick={() => props.setOpenAddModal(false)}
                                className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!traceRedact.pattern || !traceRedact.replacement}
                                className={"bg-primary h-full text-white flex flex-col justify-center p-2 rounded"}>
                                Save
                            </Button>
                        </div>
                    </div>

                    {showRegexTester && (
                        <div className="flex-1 border-l pl-8">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-textmedium">Test Your Pattern</h3>
                                <Button
                                    onClick={() => setShowRegexTester(prev => !prev)}
                                    className="bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded"
                                >
                                    Hide Tester
                                </Button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-textmedium mb-2">
                                        Test String
                                    </label>
                                    <textarea
                                        value={testString}
                                        onChange={(e) => setTestString(e.target.value)}
                                        className="w-full min-h-[100px] p-3 bg-backgrounddark border border-border text-textmedium rounded font-mono"
                                        placeholder="Enter your test string here"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-textmedium mb-2">
                                        Result with Redaction Applied
                                    </label>
                                    <div
                                        className="w-full min-h-[100px] p-3 bg-backgrounddark border border-border text-textmedium rounded font-mono whitespace-pre-wrap">
                                        {redactedOutput}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
